import type {
  ListResponse,
  LookupAiActClass,
  LookupBlockedReason,
  LookupBusinessUnit,
  LookupStage,
} from '@/domain/types'
import { useApiQuery, type QueryState } from '@/api/useApiQuery'

/**
 * The four funnel picklists.
 *
 * Grouped in one module rather than four near-identical files: each is the
 * same three lines around a different path, and `useLookupDomains` /
 * `useLookupCapabilities` / `useLookupUsers` keep their own files only
 * because they predate the funnel.
 *
 * `useLookupStatuses` is gone. `/api/lookup/statuses` returns 404 — a
 * caller still asking for statuses is running against the old model, and a
 * 404 says so more usefully than a payload of unrecognisable codes.
 */

function unwrap<T>(query: QueryState<ListResponse<T>>): QueryState<T[]> {
  if (query.status === 'success') return { status: 'success', data: query.data.data }
  return query
}

export function useLookupStages(): QueryState<LookupStage[]> {
  return unwrap(useApiQuery<ListResponse<LookupStage>>('/api/lookup/stages'))
}

export function useLookupBusinessUnits(): QueryState<LookupBusinessUnit[]> {
  return unwrap(useApiQuery<ListResponse<LookupBusinessUnit>>('/api/lookup/business-units'))
}

export function useLookupBlockedReasons(): QueryState<LookupBlockedReason[]> {
  return unwrap(useApiQuery<ListResponse<LookupBlockedReason>>('/api/lookup/blocked-reasons'))
}

export function useLookupAiActClasses(): QueryState<LookupAiActClass[]> {
  return unwrap(useApiQuery<ListResponse<LookupAiActClass>>('/api/lookup/ai-act-classes'))
}
